import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Calendar as CalendarIcon, CheckCircle2, AlertTriangle, Loader2, ArrowLeft, Power } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CalendarAdmin() {
  const [params] = useSearchParams();
  const [status, setStatus] = useState({ loading: true });

  const refresh = async () => {
    setStatus((s) => ({ ...s, loading: true }));
    try {
      const res = await axios.get(`${API}/oauth/google/status`);
      setStatus({ loading: false, ...res.data });
    } catch {
      setStatus({ loading: false, error: "Could not load connection status." });
    }
  };

  useEffect(() => {
    refresh();
    const result = params.get("status");
    if (result === "connected") toast.success("Google Calendar connected.");
    if (result === "error") {
      const reason = params.get("reason") || "unknown";
      const email = params.get("email");
      if (reason === "wrong_account") {
        toast.error(`Connected with the wrong account (${email}). Please retry and sign in as Monica.`);
      } else if (reason === "no_refresh_token") {
        toast.error("Google didn't return a refresh token. Please remove the app from your Google account permissions and try again.");
      } else {
        toast.error(`Connection failed: ${reason}`);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connect = () => {
    window.location.href = `${API}/oauth/google/start`;
  };

  const disconnect = async () => {
    try {
      await axios.post(`${API}/oauth/google/disconnect`);
      toast.success("Disconnected.");
      refresh();
    } catch {
      toast.error("Failed to disconnect.");
    }
  };

  return (
    <main
      data-testid="admin-calendar-page"
      className="min-h-screen flex items-center justify-center px-6"
      style={{ background: "var(--bg-primary)" }}
    >
      <div className="w-full max-w-xl">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm mb-8 hover:underline"
          style={{ color: "var(--text-muted)" }}
          data-testid="admin-back-home"
        >
          <ArrowLeft size={14} /> Back to site
        </Link>

        <div
          className="rounded-3xl p-10 brand-soft-shadow"
          style={{ background: "var(--bg-tertiary)", border: "1px solid var(--border-soft)" }}
        >
          <div
            className="w-14 h-14 rounded-full flex items-center justify-center mb-6"
            style={{ background: "var(--bg-secondary)" }}
          >
            <CalendarIcon size={22} color="var(--blush-hover)" />
          </div>

          <span className="section-eyebrow">Admin · For Monica</span>
          <h1 className="font-serif-display text-4xl mt-2 mb-3" style={{ color: "var(--text-main)" }}>
            Google Calendar Sync
          </h1>
          <p className="text-sm leading-relaxed" style={{ color: "var(--text-muted)" }}>
            Connect your Google Calendar so the website hides time slots that conflict with events on
            your calendar, and auto-creates new bookings as events with a Google Meet link.
          </p>

          <div
            className="mt-8 rounded-2xl p-5 flex items-start gap-3"
            style={{
              background: status.connected ? "rgba(192,168,128,0.10)" : "var(--bg-secondary)",
              border: `1px solid ${status.connected ? "var(--gold)" : "var(--border-soft)"}`,
            }}
            data-testid="admin-status-banner"
          >
            {status.loading ? (
              <Loader2 size={18} className="animate-spin mt-0.5" color="var(--text-muted)" />
            ) : status.connected ? (
              <CheckCircle2 size={18} color="var(--gold)" className="mt-0.5" />
            ) : (
              <AlertTriangle size={18} color="var(--blush)" className="mt-0.5" />
            )}
            <div className="flex-1 text-sm" style={{ color: "var(--text-main)" }}>
              {status.loading && "Checking connection…"}
              {!status.loading && status.connected && (
                <>
                  <b>Connected.</b> Bookings will sync to <span style={{ color: "var(--gold)" }}>{status.email}</span>.
                </>
              )}
              {!status.loading && !status.connected && status.configured && (
                <>
                  <b>Not connected.</b> Click below to authorise{" "}
                  <span style={{ color: "var(--gold)" }}>{status.email || "Monica's calendar"}</span>.
                </>
              )}
              {!status.loading && !status.configured && (
                <>Google OAuth is not configured on the server.</>
              )}
            </div>
          </div>

          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            {!status.connected && (
              <Button
                onClick={connect}
                disabled={status.loading || !status.configured}
                data-testid="admin-connect-btn"
                className="rounded-full h-12 px-7 transition-transform hover:scale-105"
                style={{ background: "var(--blush)", color: "#FAF9F6" }}
              >
                <CalendarIcon size={16} className="mr-2" /> Connect Google Calendar
              </Button>
            )}
            {status.connected && (
              <Button
                onClick={disconnect}
                variant="outline"
                data-testid="admin-disconnect-btn"
                className="rounded-full h-12 px-7 border-2"
                style={{
                  borderColor: "var(--blush)",
                  color: "var(--blush-hover)",
                  background: "transparent",
                }}
              >
                <Power size={16} className="mr-2" /> Disconnect
              </Button>
            )}
          </div>

          <p className="text-xs mt-8 leading-relaxed" style={{ color: "var(--text-muted)" }}>
            We only request calendar read &amp; write access. Tokens are stored on the server. You can
            revoke access any time from your{" "}
            <a
              href="https://myaccount.google.com/permissions"
              target="_blank"
              rel="noopener noreferrer"
              className="underline"
            >
              Google account permissions
            </a>
            .
          </p>
        </div>
      </div>
    </main>
  );
}
